import React, { useState, useEffect } from 'react';
import { post, get, del, patch } from 'aws-amplify/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../utils/dateUtils';
import './Specials.css';

// Chula's brand colors (from Swift ColorScheme.swift)
const CHULAS_COLORS = [
  { hex: '#F05960', name: 'Chula\'s Red', pantone: 'PANTONE 17-1656 TPX' },
  { hex: '#7060A9', name: 'Chula\'s Purple', pantone: 'PANTONE 18-3840 TPX' },
  { hex: '#D6DE23', name: 'Chula\'s Lime', pantone: 'PANTONE 13-0651 TCX' },
  { hex: '#F8A054', name: 'Chula\'s Orange', pantone: 'PANTONE 15-1160 TPX' },
  { hex: '#FFDF8A', name: 'Chula\'s Tan', pantone: 'PANTONE 12-0727 TCX' },
  { hex: '#00B19A', name: 'Chula\'s Teal', pantone: 'PANTONE 16-5427 TCX' },
  { hex: '#231F20', name: 'Chula\'s Black', pantone: 'PANTONE 19-3911 TCX' },
];

// Available icons for specials
const SPECIAL_ICONS = [
  { id: 'taco', emoji: '🌮', label: 'Taco' },
  { id: 'burger', emoji: '🍔', label: 'Burger' },
  { id: 'pizza', emoji: '🍕', label: 'Pizza' },
  { id: 'beer', emoji: '🍺', label: 'Beer' },
  { id: 'cocktail', emoji: '🍹', label: 'Cocktail' },
  { id: 'wine', emoji: '🍷', label: 'Wine' },
  { id: 'champagne', emoji: '🥂', label: 'Champagne' },
  { id: 'coffee', emoji: '☕', label: 'Coffee' },
  { id: 'breakfast', emoji: '🍳', label: 'Breakfast' },
  { id: 'brunch', emoji: '🥞', label: 'Brunch' },
  { id: 'steak', emoji: '🥩', label: 'Steak' },
  { id: 'salad', emoji: '🥗', label: 'Salad' },
  { id: 'sushi', emoji: '🍣', label: 'Sushi' },
  { id: 'noodles', emoji: '🍜', label: 'Noodles' },
  { id: 'dessert', emoji: '🍰', label: 'Dessert' },
  { id: 'icecream', emoji: '🍦', label: 'Ice Cream' },
  { id: 'fire', emoji: '🔥', label: 'Hot Deal' },
  { id: 'star', emoji: '⭐', label: 'Featured' },
  { id: 'party', emoji: '🎉', label: 'Party' },
  { id: 'music', emoji: '🎵', label: 'Live Music' },
  { id: 'clock', emoji: '⏰', label: 'Limited Time' },
  { id: 'calendar', emoji: '📅', label: 'Event' },
  { id: 'sparkle', emoji: '✨', label: 'Special' },
  { id: 'gift', emoji: '🎁', label: 'Gift' },
];

const Specials = () => {
  const [specials, setSpecials] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#7060A9'); // Chula's Purple default
  const [icon, setIcon] = useState(''); // Empty = no icon
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [editingSpecial, setEditingSpecial] = useState(null);
  const { getAuthToken } = useAuth();

  // Fetch specials on component mount
  useEffect(() => {
    fetchSpecials();
  }, []);

  const fetchSpecials = async () => {
    try {
      setLoadingList(true);
      setError(null);

      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: '/v1/specials',
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setSpecials(data.specials || []);
    } catch (err) {
      console.error('Error fetching specials:', err);
      setError('Failed to fetch specials');
    } finally {
      setLoadingList(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setImageFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadImage = async (specialId, token) => {
    if (!imageFile) return null;

    try {
      const reader = new FileReader();

      return new Promise((resolve, reject) => {
        reader.onloadend = async () => {
          try {
            const base64Image = reader.result;

            const restOperation = post({
              apiName: 'ChulasAdminAPI',
              path: `/v1/admin/specials/${specialId}/upload-image`,
              options: {
                headers: {
                  Authorization: token,
                },
                body: {
                  image_data: base64Image,
                  content_type: imageFile.type,
                },
              },
            });

            const response = await restOperation.response;
            const data = await response.body.json();
            resolve(data.image_url);
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(imageFile);
      });
    } catch (err) {
      console.error('Error uploading image:', err);
      throw new Error('Failed to upload image');
    }
  };

  const createSpecial = async (e) => {
    e.preventDefault();

    if (!title.trim() || !description.trim()) {
      setError('Please enter title and description');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      // Create the special first
      const restOperation = post({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/specials',
        options: {
          headers: {
            Authorization: token,
          },
          body: {
            title: title.trim(),
            description: description.trim(),
            color: color,
            icon: icon,
          },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();
      const specialId = data.special.special_id;

      // Upload image if selected
      if (imageFile) {
        await uploadImage(specialId, token);
      }

      setSuccessMessage('Special created successfully!');
      setTitle('');
      setDescription('');
      setColor('#7060A9');
      setIcon('');
      setImageFile(null);
      setImagePreview(null);

      // Refresh the specials list
      await fetchSpecials();
    } catch (err) {
      console.error('Error creating special:', err);
      setError(err.message || 'Failed to create special. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateSpecial = async (e) => {
    e.preventDefault();

    if (!editingSpecial) return;

    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      // Update the special
      const restOperation = patch({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/specials/${editingSpecial.special_id}`,
        options: {
          headers: {
            Authorization: token,
          },
          body: {
            title: title.trim(),
            description: description.trim(),
            color: color,
            icon: icon,
          },
        },
      });

      await restOperation.response;

      // Upload image if selected
      if (imageFile) {
        await uploadImage(editingSpecial.special_id, token);
      }

      setSuccessMessage('Special updated successfully!');
      setEditingSpecial(null);
      setTitle('');
      setDescription('');
      setColor('#7060A9');
      setIcon('');
      setImageFile(null);
      setImagePreview(null);

      // Refresh the specials list
      await fetchSpecials();
    } catch (err) {
      console.error('Error updating special:', err);
      setError(err.message || 'Failed to update special. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const startEditSpecial = (special) => {
    setEditingSpecial(special);
    setTitle(special.title);
    setDescription(special.description);
    setColor(special.color || '#7060A9');
    setIcon(special.icon || '');
    setImagePreview(special.image_url || null);
    setImageFile(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingSpecial(null);
    setTitle('');
    setDescription('');
    setColor('#7060A9');
    setIcon('');
    setImageFile(null);
    setImagePreview(null);
  };

  const deleteSpecial = async (specialId) => {
    if (!window.confirm('Are you sure you want to delete this special?')) {
      return;
    }

    try {
      setDeletingId(specialId);
      setError(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = del({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/specials/${specialId}`,
        options: {
          headers: {
            Authorization: token,
          },
        },
      });

      await restOperation.response;

      setSuccessMessage('Special deleted successfully!');

      // Refresh the specials list
      await fetchSpecials();
    } catch (err) {
      console.error('Error deleting special:', err);
      setError(err.message || 'Failed to delete special. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="specials-container">
      <h1>Specials Management</h1>
      <p className="page-description">
        Create and manage specials that will be displayed in the mobile app's home screen.
      </p>

      {/* Create/Edit Special Form */}
      <div className="special-form-section">
        <h2>{editingSpecial ? 'Edit Special' : 'Create New Special'}</h2>
        <form onSubmit={editingSpecial ? updateSpecial : createSpecial} className="special-form">
          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Taco Tuesday"
              disabled={loading}
              maxLength={50}
            />
            <small className="character-count">
              {title.length}/50 characters
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., $2 tacos all day"
              rows="3"
              disabled={loading}
              maxLength={100}
            />
            <small className="character-count">
              {description.length}/100 characters
            </small>
          </div>

          <div className="form-group">
            <label>Color</label>
            <div className="color-presets">
              {CHULAS_COLORS.map((chulasColor) => (
                <button
                  key={chulasColor.hex}
                  type="button"
                  className={`color-preset ${color.toUpperCase() === chulasColor.hex.toUpperCase() ? 'selected' : ''}`}
                  onClick={() => setColor(chulasColor.hex)}
                  disabled={loading}
                  title={chulasColor.name}
                  style={{ backgroundColor: chulasColor.hex }}
                >
                  {color.toUpperCase() === chulasColor.hex.toUpperCase() && (
                    <span className="checkmark">✓</span>
                  )}
                </button>
              ))}
            </div>
            <small className="color-presets-label">Chula's Brand Colors</small>

            <div className="color-picker-group">
              <input
                type="color"
                id="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                disabled={loading}
              />
              <input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value.toUpperCase())}
                placeholder="#7060A9"
                disabled={loading}
                maxLength={7}
                className="color-text-input"
              />
            </div>
            <small>Or choose a custom color</small>
          </div>

          <div className="form-group">
            <label>Icon (optional)</label>
            <div className="icon-picker">
              <button
                key="none"
                type="button"
                className={`icon-option ${icon === '' ? 'selected' : ''}`}
                onClick={() => setIcon('')}
                disabled={loading}
                title="No Icon"
              >
                <span className="icon-emoji">🚫</span>
                <span className="icon-label">None</span>
              </button>
              {SPECIAL_ICONS.map((iconOption) => (
                <button
                  key={iconOption.id}
                  type="button"
                  className={`icon-option ${icon === iconOption.id ? 'selected' : ''}`}
                  onClick={() => setIcon(iconOption.id)}
                  disabled={loading}
                  title={iconOption.label}
                >
                  <span className="icon-emoji">{iconOption.emoji}</span>
                  <span className="icon-label">{iconOption.label}</span>
                </button>
              ))}
            </div>
            <small>Choose an icon to display on the special card (or select "None")</small>
          </div>

          <div className="form-group">
            <label htmlFor="image">Image (optional)</label>
            <input
              type="file"
              id="image"
              accept="image/*"
              onChange={handleImageChange}
              disabled={loading}
            />
            <small>Max file size: 5MB. Supported formats: JPG, PNG, GIF</small>
            {imagePreview && (
              <div className="image-preview">
                <img src={imagePreview} alt="Preview" />
              </div>
            )}
          </div>

          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="alert alert-success">
              {successMessage}
            </div>
          )}

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !title.trim() || !description.trim()}
            >
              {loading ? (editingSpecial ? 'Updating...' : 'Creating...') : (editingSpecial ? 'Update Special' : 'Create Special')}
            </button>
            {editingSpecial && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={cancelEdit}
                disabled={loading}
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Specials List */}
      <div className="specials-list-section">
        <div className="list-header">
          <h2>Current Specials</h2>
          <button onClick={fetchSpecials} className="btn btn-secondary" disabled={loadingList}>
            {loadingList ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {loadingList ? (
          <div className="loading-spinner">Loading specials...</div>
        ) : specials.length === 0 ? (
          <div className="empty-state">
            <p>No active specials</p>
            <small>Create your first special above to get started</small>
          </div>
        ) : (
          <div className="specials-grid">
            {specials.map((special) => {
              const specialIcon = SPECIAL_ICONS.find(i => i.id === special.icon);

              return (
                <div key={special.special_id} className="special-card">
                  {special.image_url && (
                    <div className="special-image">
                      <img src={special.image_url} alt={special.title} />
                    </div>
                  )}
                  <div
                    className="special-content"
                    style={{ backgroundColor: special.color || '#7060A9' }}
                  >
                    {specialIcon && (
                      <div className="special-icon">
                        {specialIcon.emoji}
                      </div>
                    )}
                    <div className="special-header">
                      <h3>{special.title}</h3>
                      <span className="special-date">
                        {formatDateTime(special.created_at)}
                      </span>
                    </div>
                    <p className="special-description">{special.description}</p>
                  </div>
                  <div className="special-actions">
                    <button
                      onClick={() => startEditSpecial(special)}
                      className="btn-edit"
                      disabled={loading}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteSpecial(special.special_id)}
                      className="btn-delete"
                      disabled={deletingId === special.special_id}
                    >
                      {deletingId === special.special_id ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Specials;
